import {z} from "zod"

const NormalizeMonetaryAmountParamsSchema = z
  .object({
    amount: z.number().optional(),
    amountMinor: z.number().int().optional(),
    currency: z.string().min(3).max(3).toUpperCase(),
  })
  .refine(
    (data) =>
      (data.amount !== undefined && data.amount !== 0) || (data.amountMinor !== undefined && data.amountMinor !== 0),
    {
      message: "either amount or amountMinor must be provided and non-zero",
      path: ["amount", "amountMinor"],
    }
  )

/**
 * normalizes monetary amount input to ensure both amount and amountMinor are always present
 *
 * @param {Object} params - monetary amount parameters
 * @param {number} [params.amount] - amount in major currency units (e.g. 1.50 for $1.50 or -1.50 for -$1.50)
 * @param {number} [params.amountMinor] - amount in minor currency units (e.g. 150 for $1.50 or -150 for -$1.50)
 * @param {string} params.currency - 3-letter currency code (e.g. "USD", "CZK")
 * @returns {Object} normalized monetary amount
 * @returns {number} returns.amount - amount in major currency units
 * @returns {number} returns.amountMinor - amount in minor currency units (rounded to avoid floating point issues)
 * @returns {string} returns.currency - uppercase 3-letter currency code
 *
 * @throws {Error} when currency is not supported
 * @throws {ZodError} when validation fails (missing amounts, invalid currency format, etc.)
 *
 * @example
 * // using amount
 * normalizeMonetaryAmount({amount: 1.50, currency: "USD"})
 * // returns {amount: 1.50, amountMinor: 150, currency: "USD"}
 *
 * @example
 * // using amountMinor
 * normalizeMonetaryAmount({amountMinor: -150, currency: "USD"})
 * // returns {amount: -1.50, amountMinor: -150, currency: "USD"}
 */
function normalizeMonetaryAmount(params) {
  const {amount, amountMinor, currency} = NormalizeMonetaryAmountParamsSchema.parse(params)

  const currencyMinorUnitRatio = getCurrencyMinorUnitRatio(currency)

  const hasAmount = amount !== undefined && amount !== 0
  const hasAmountMinor = amountMinor !== undefined && amountMinor !== 0

  // throw error if both amount and amountMinor are provided
  if (hasAmount && hasAmountMinor) {
    throw new Error("Cannot provide both amount and amountMinor", {
      cause: {
        code: "both_amounts_provided",
      },
    })
  }

  const result = {
    amount: hasAmount ? amount : amountMinor / currencyMinorUnitRatio,
    amountMinor: Math.round(hasAmountMinor ? amountMinor : amount * currencyMinorUnitRatio), // round to avoid floating point precision issues
    currency,
  }

  return result
}

function getCurrencyMinorUnitRatio(currency) {
  // TODO: list all currencies
  // this is just to find this snippet via code search until it is refactored everywhere:
  // const currencyMinorUnitRatio = 100 // eg. 100 for CZK (100 halers per 1 CZK). for JPY it is 1 (1 yen per 1 yen)
  const currencyMinorUnitRatios = {
    CZK: 100,
    USD: 100,
    EUR: 100,
    GBP: 100,
    AED: 100,
  }

  const currencyMinorUnitRatio = currencyMinorUnitRatios[currency]

  if (!currencyMinorUnitRatio) {
    throw new Error(`Currency ${currency} is not supported`, {
      cause: {
        code: "currency_not_supported",
      },
    })
  }

  return currencyMinorUnitRatio
}

export {normalizeMonetaryAmount}
